import React, { createContext, useContext, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { resolveAdminPermission } from '../constants/adminPermissionCatalog';
import { getAdminContractBadgeSummary, listUpcomingHoldRequests } from '../api/castingSummaries';
import { getAdminMonthlyPaymentBadgeSummary } from '../api/payments';

type Counts = {
  offerTaskCount: number;
  offerTaskAssignedCount: number | null;
  contractTaskCount: number;
  contractTaskAssignedCount: number | null;
  invoiceUndownloadedCount: number;
  invoiceUndownloadedTone: 'alert' | 'ok' | null;
};
type Resource = 'offers' | 'contracts' | 'payments';
const EMPTY: Counts = {
  offerTaskCount: 0, offerTaskAssignedCount: null,
  contractTaskCount: 0, contractTaskAssignedCount: null,
  invoiceUndownloadedCount: 0, invoiceUndownloadedTone: null,
};
const AdminMenuBadgeContext = createContext<Counts>(EMPTY);
const validCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

function pendingOffers(payload: unknown): number | null {
  const data = payload as { holdRequests?: unknown; hold_requests?: unknown } | null;
  const rows = data?.holdRequests ?? data?.hold_requests;
  return Array.isArray(rows)
    ? rows.filter((row) => String(row?.status || '').toLowerCase() === 'pending').length
    : null;
}

// Each store belongs to one authenticated permission scope, never to a page.
function createBadgeStore(allowed: Record<Resource, boolean>) {
  let snapshot = EMPTY;
  let generation = 0;
  let active = false;
  const listeners = new Set<() => void>();
  const inFlight = new Map<Resource, Promise<void>>();
  const queued = new Set<Resource>();
  const update = (patch: Partial<Counts>, requestGeneration: number) => {
    if (!active || generation !== requestGeneration) return;
    snapshot = { ...snapshot, ...patch };
    listeners.forEach((listener) => listener());
  };
  async function fetchCounts(resource: Resource, requestGeneration: number) {
    if (resource === 'offers') {
      const [all, assigned] = await Promise.allSettled([
        listUpcomingHoldRequests(), listUpcomingHoldRequests({ assigned_to_me: true }),
      ]);
      const count = all.status === 'fulfilled' ? pendingOffers(all.value) : null;
      const assignedCount = assigned.status === 'fulfilled' ? pendingOffers(assigned.value) : null;
      update({
        ...(count === null ? {} : { offerTaskCount: count }),
        ...(assignedCount === null ? {} : { offerTaskAssignedCount: assignedCount }),
      }, requestGeneration);
    } else if (resource === 'contracts') {
      const { data } = await getAdminContractBadgeSummary();
      if (!validCount(data?.pendingCount)) return;
      update({
        contractTaskCount: data.pendingCount,
        ...(validCount(data.assignedPendingCount)
          ? { contractTaskAssignedCount: data.assignedPendingCount } : {}),
      }, requestGeneration);
    } else {
      const { data } = await getAdminMonthlyPaymentBadgeSummary();
      if (!validCount(data?.undownloadedCount)) return;
      update({
        invoiceUndownloadedCount: data.undownloadedCount,
        ...(validCount(data.assignedUndownloadedCount)
          ? { invoiceUndownloadedTone: data.assignedUndownloadedCount > 0 ? 'alert' : 'ok' } : {}),
      }, requestGeneration);
    }
  }
  const refresh = (resource: Resource, force = false) => {
    if (!active || !allowed[resource]) return;
    if (inFlight.has(resource)) {
      // A mutation during a read needs one follow-up read; route changes share the current one.
      if (force) queued.add(resource);
      return;
    }
    const requestGeneration = generation;
    const request = fetchCounts(resource, requestGeneration)
      .catch(() => { /* Background failures retain the last successful count and tone. */ })
      .finally(() => {
        if (inFlight.get(resource) !== request) return;
        inFlight.delete(resource);
        if (queued.delete(resource)) refresh(resource);
      });
    inFlight.set(resource, request);
  };
  return {
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    activate: () => {
      active = true;
      return () => {
        active = false;
        generation += 1;
        inFlight.clear();
        queued.clear();
      };
    },
    refresh,
  };
}

export function AdminMenuBadgeProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth() || {};
  const { pathname } = useLocation();
  const isAdminPath = pathname.startsWith('/admin/');
  const isAdmin = user?.userType === 'admin';
  // Include field/event restrictions, even if the navigation permission itself is unchanged.
  const scope = JSON.stringify(isAdmin ? [
    user.userId ?? user.id, user.role, user.is_external_account,
    user.permissions, user.permission_entries,
  ] : null);
  const store = useMemo(() => {
    const identity = JSON.parse(scope);
    const [, role, isExternalAccount, rawPermissions] = identity || [];
    const permissions = Array.isArray(rawPermissions) ? rawPermissions : [rawPermissions].filter(Boolean);
    const can = (permissionKey: string) => Boolean(identity) && (
      role === 'admin' || permissions.includes('admin') || permissions.includes('*') ||
      resolveAdminPermission({ role: role || 'general', isExternalAccount, permissions, permissionKey })
    );
    return createBadgeStore({
      offers: can('NAV_CASTING_OFFER'), contracts: can('NAV_CASTING_CONTRACT'), payments: can('NAV_PAYMENTS'),
    });
  }, [scope]);
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  useEffect(() => store.activate(), [store]);
  useEffect(() => {
    if (!isAdminPath) return;
    store.refresh('offers');
    store.refresh('contracts');
    store.refresh('payments');
  }, [store, pathname, isAdminPath]);
  useEffect(() => {
    if (!isAdminPath) return;
    const offers = () => store.refresh('offers', true);
    const contracts = () => store.refresh('contracts', true);
    const peer = (event: Event) => {
      const type = (event as CustomEvent).detail?.type;
      if (type === 'additional_reward.claim_submitted' || type === 'additional_reward.claim_completed') contracts();
    };
    window.addEventListener('offers:refresh-task-count', offers);
    window.addEventListener('contracts:refresh-task-count', contracts);
    window.addEventListener('peer:notification', peer);
    return () => {
      window.removeEventListener('offers:refresh-task-count', offers);
      window.removeEventListener('contracts:refresh-task-count', contracts);
      window.removeEventListener('peer:notification', peer);
    };
  }, [store, isAdminPath]);
  return <AdminMenuBadgeContext.Provider value={snapshot}>{children}</AdminMenuBadgeContext.Provider>;
}

export const useAdminMenuBadges = () => useContext(AdminMenuBadgeContext);
